import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Printer, Wrench, Clock, ThumbsUp, DollarSign, Award, ShieldCheck, TrendingUp, Coffee } from "lucide-react";
import DemoQRCode from "@/components/DemoQRCode";

const WINS = [
  {
    Icon: Wrench,
    tint: "bg-navy",
    title: "Your MPI actually gets read now",
    body: "Yellow and red items you flag on the iPad go straight to a text the customer sees. No more \"the advisor forgot to call them\" — the app calls them for you.",
  },
  {
    Icon: DollarSign,
    tint: "bg-emerald-600",
    title: "More work coming back to your bay",
    body: "Every yellow/red item is a future RO. When that customer books back in, service will steer that work to the tech who caught it — real cash flag hours on the work you already inspected.",
  },
  {
    Icon: Clock,
    tint: "bg-sky-600",
    title: "Less arguing at the desk",
    body: "When the advisor tells the customer \"you declined the brakes 7 days ago\", the app has the timestamp, the color code, and your name attached. No he-said-she-said.",
  },
  {
    Icon: Award,
    tint: "bg-[hsl(var(--gold))] text-neutral-900",
    title: "Your scorecard, your reputation",
    body: "Every dealer principal sees the Technician Scorecard — inspections done, yellow flags, red flags, revenue that came back because of you. Top techs get noticed.",
  },
  {
    Icon: ShieldCheck,
    tint: "bg-purple-600",
    title: "Safety-first — you're not the bad guy",
    body: "You called out a bad tie rod. The customer said no. Two weeks later, the app quietly texted them a discount nudge. If they'd wrecked, your name on that MPI is legal cover for the store AND you.",
  },
  {
    Icon: TrendingUp,
    tint: "bg-orange-600",
    title: "Faster mornings",
    body: "Comebacks page shows advisors who to call today. Techs get first crack at their own returning customers instead of whichever bay is open. You built the relationship — you keep it.",
  },
  {
    Icon: Coffee,
    tint: "bg-rose-600",
    title: "You're not learning software",
    body: "Nothing to install. Nothing to type. Nothing to \"log in and check.\" You tap green/yellow/red on the iPad, hand it to your advisor, and go back to turning wrenches. That's it.",
  },
  {
    Icon: ThumbsUp,
    tint: "bg-cyan-600",
    title: "Customers actually come back",
    body: "Industry avg: 8-12% of declined recommendations come back within 90 days. Decline Drive stores are seeing 18-25%. That's real ROs sitting in front of you next month because you did your inspection right today.",
  },
];

export default function TechnicianHandoutPage() {
  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="For the Technicians" />

      <div className="mb-6 flex items-start justify-between gap-4 no-print">
        <div>
          <div className="font-mono-label text-muted-foreground">Tech-Facing Handout</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">
            Why This System Helps YOU
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Hand this to every technician on Day 1 of onboarding. Straight talk, no corporate speak — what changes for the wrench-turners when Decline Drive shows up.
          </p>
        </div>
        <Button onClick={() => window.print()} data-testid="print-tech-handout-btn">
          <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Print
        </Button>
      </div>

      {/* Hero opener */}
      <Card className="p-6 border-[hsl(var(--gold))]/40 bg-gradient-to-br from-navy to-[#0a1d4a] text-white mb-6" data-testid="tech-hero">
        <div className="font-mono-label text-[hsl(var(--gold-light))] text-[0.65rem] tracking-widest">
          Straight to the point
        </div>
        <div className="font-display font-black text-2xl md:text-3xl tracking-tight mt-2 leading-tight">
          &quot;Decline Drive isn&apos;t here to make you fill out more paperwork. It&apos;s here to make sure the work you&apos;re already flagging on the MPI actually comes back through your bay.&quot;
        </div>
      </Card>

      {/* 8 wins */}
      <div className="grid md:grid-cols-2 gap-4">
        {WINS.map((w, i) => (
          <Card key={i} className="p-0 overflow-hidden border-border" data-testid={`tech-win-${i}`}>
            <div className="flex items-stretch h-full">
              <div className={`w-20 shrink-0 ${w.tint} text-white grid place-items-center p-3`}>
                <w.Icon className="w-6 h-6" strokeWidth={2} />
              </div>
              <div className="flex-1 p-4">
                <div className="font-display font-bold text-base tracking-tight">{w.title}</div>
                <div className="text-sm text-muted-foreground mt-1 leading-relaxed">{w.body}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Bottom line */}
      <Card className="p-6 border-[hsl(var(--gold))] bg-[hsl(var(--gold))]/5 mt-6" data-testid="tech-bottom-line">
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="flex-1">
            <div className="font-mono-label text-[hsl(var(--gold))] text-[0.6rem] tracking-widest">
              Bottom line
            </div>
            <div className="font-display font-black text-xl md:text-2xl tracking-tight mt-2 leading-tight">
              More come-back work. More flag hours. Less &quot;did anyone call them?&quot; A scoreboard the dealer principal can&apos;t ignore.
            </div>
            <div className="text-sm text-muted-foreground mt-3 italic">
              Questions? Grab your service manager. They&apos;ll walk you through the iPad in 5 minutes.
            </div>
          </div>
          <DemoQRCode size={110} caption="Scan · see how it works" />
        </div>
      </Card>
    </DashboardShell>
  );
}
